import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
} from '@mui/material'
import { useState } from 'react'
import axios from 'axios'
import { useNavigate } from 'react-router-dom'
import './SupplierQuoteNumberDialog.css'
import { ComplianceStatus, quoteLineType, supplierQuoteImportStateType } from '../../types'

export default function SupplierQuoteNumberDialog({
  open,
  onClose,
  quoteId,
  supplierName,
  endpoint,
  fieldLabel,
  bodyKey,
}: {
  open: boolean
  onClose: () => void
  quoteId: string
  supplierName: string
  endpoint: string
  fieldLabel: string
  bodyKey: string
}) {
  const navigate = useNavigate()
  const [quoteNumber, setQuoteNumber] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)
  const [pendingImportState, setPendingImportState] = useState<supplierQuoteImportStateType | null>(null)

  const handleClose = () => {
    setQuoteNumber('')
    setError(false)
    setLoading(false)
    onClose()
  }

  const goToValidationScreen = (state: supplierQuoteImportStateType) => {
    navigate(`/quote/${quoteId}/validation`, { state })
  }

  const handleSubmit = () => {
    setLoading(true)
    setError(false)
    axios
      .post(`/quote/${quoteId}/${endpoint}`, { [bodyKey]: quoteNumber })
      .then(async res => {
        const lineItems: quoteLineType[] = res.data.lineItems
        const { data: scrmResults } = await axios.post<{ LineNumber: number; Scrm: ComplianceStatus }[]>(
          `/quote/${quoteId}/checkScrmCompliance`,
          { lineItems },
        )
        const scrmByLine = new Map<number, ComplianceStatus>(scrmResults.map(r => [r.LineNumber, r.Scrm]))

        const state: supplierQuoteImportStateType = {
          quoteId,
          lineItems: lineItems.map(item => ({ ...item, Scrm: scrmByLine.get(item.LineNumber) ?? null })),
          quoteNumber: res.data.header.vendorQuoteNumber,
          supplier: res.data.header.vendorName,
          totalAmount: res.data.header.totalAmount,
          expirationDate: res.data.header.expirationDate ? new Date(res.data.header.expirationDate) : null,
          file: null,
        }

        const hasNonCompliantLine = state.lineItems.some(item => item.Scrm === ComplianceStatus.NotCompliant)
        if (hasNonCompliantLine) {
          setLoading(false)
          setPendingImportState(state)
          return
        }

        goToValidationScreen(state)
      })
      .catch(err => {
        console.error(`Failed to fetch ${supplierName} quote`, err)
        setError(true)
        setLoading(false)
      })
  }

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="xs">
      <DialogTitle variant="h6">Import {supplierName} Quote</DialogTitle>
      <DialogContent className="supplier-quote-number-dialog-content">
        <TextField fullWidth label={fieldLabel} value={quoteNumber} onChange={e => setQuoteNumber(e.target.value)} />
        {error && (
          <Alert severity="error">
            Couldn't find that {supplierName} quote. Double check the quote number and try again.
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={handleClose}>
          Cancel
        </Button>
        <Button variant="contained" disabled={!quoteNumber} loading={loading} onClick={handleSubmit}>
          Submit
        </Button>
      </DialogActions>
      <Dialog open={pendingImportState !== null} onClose={() => setPendingImportState(null)} maxWidth="xs" fullWidth>
        <DialogTitle fontWeight={600}>Not SCRM Compliant</DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            The following Suppliers/Manufacturers are not SCRM compliant:
            <Box component="ul" marginY={1} paddingLeft={3}>
              {Array.from(
                new Set(
                  (pendingImportState?.lineItems ?? [])
                    .filter(item => item.Scrm === ComplianceStatus.NotCompliant)
                    .map(
                      item =>
                        `${item.Manufacturer ?? 'Unknown Manufacturer'} (Supplier: ${
                          item.Provider ?? 'Unknown Supplier'
                        })`,
                    ),
                ),
              ).map(entry => (
                <li key={entry}>
                  <strong>{entry}</strong>
                </li>
              ))}
            </Box>
            Request or Add a new Supplier/Manufacturer to import this quote.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => setPendingImportState(null)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  )
}
